// rasterdekking.js — legt een raster over een gemeente en bepaalt per vakje
// of er een laadpunt binnen loopafstand staat.
//
// Voor het verdelen van hele palen over de wijken wordt dezelfde verdeelregel
// gebruikt als in het scherm, zodat er maar een plek is waar die regel staat.
//
// Waarom een raster en niet de vormen zelf: het gebied dat buiten alle cirkels
// valt is meetkundig lastig uit te rekenen, maar met een raster is het simpel
// tellen. Elk vakje weet in welke sector het ligt, hoeveel inwoners daarbij
// horen en of er een laadpunt dichtbij staat. Daarmee zijn de witte vlekken te
// kleuren en is te zeggen hoeveel mensen erbuiten wonen.
//
// Alles rekent in METERS in een plat vlak rond de gemeente. Over een gebied van
// enkele tientallen kilometers is die vereenvoudiging ruim nauwkeurig genoeg,
// zeker tegenover een rastervakje van vijftig meter.

import { verdeelHeel } from './gemeenteData';

// Waarmerk van deze versie. Staat hier zodat in de browser te zien is welke
// versie van dit bestand er werkelijk draait, zonder in de code te hoeven
// zoeken. Bij elke oplevering bijwerken.
export const RASTER_VERSIE = '27 oktober 2026, wijk impact laadpleinen';

const METER_PER_GRAAD = 111320;

// ── Hulpjes voor vormen ──────────────────────────────────────────────
function vlakkenVan(geom) {
  if (!geom) return [];
  if (geom.type === 'Polygon') return [geom.coordinates];
  if (geom.type === 'MultiPolygon') return geom.coordinates;
  return [];
}

function puntInRing(x, y, ring) {
  let binnen = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i][0], yi = ring[i][1];
    const xj = ring[j][0], yj = ring[j][1];
    if (((yi > y) !== (yj > y)) &&
        (x < (xj - xi) * (y - yi) / ((yj - yi) || 1e-12) + xi)) binnen = !binnen;
  }
  return binnen;
}

/**
 * Bouwt het raster over alle meegegeven sectoren.
 *
 * @param {Array}  sectoren  kenmerken uit de kaartgegevens, elk met geometry en
 *                           properties.NISCODE en properties.WIJK_ID
 * @param {Object} inwoners  sectorcode naar aantal inwoners
 * @param {number} vakM      zijde van een rastervakje in meters
 * @param {Array}  adressen  punten uit het Adressenregister, elk { lat, lng }.
 *                           Zijn ze er, dan worden de inwoners van een sector
 *                           over zijn vakjes verdeeld naar het aantal adressen
 *                           in elk vakje. Zijn ze er niet, dan gebeurt dat
 *                           gelijkmatig, zoals voorheen.
 */
export function bouwRaster(sectoren, inwoners = {}, vakM = 50, adressen = null) {
  const vormen = (sectoren || []).filter(f => f?.geometry);
  if (!vormen.length) return null;

  // Middelpunt van het gebied, om graden naar meters om te rekenen.
  let minLon = Infinity, maxLon = -Infinity, minLat = Infinity, maxLat = -Infinity;
  for (const f of vormen) {
    for (const vlak of vlakkenVan(f.geometry)) {
      for (const [lon, lat] of (vlak[0] || [])) {
        if (!Number.isFinite(lon) || !Number.isFinite(lat)) continue;
        if (lon < minLon) minLon = lon; if (lon > maxLon) maxLon = lon;
        if (lat < minLat) minLat = lat; if (lat > maxLat) maxLat = lat;
      }
    }
  }
  if (!Number.isFinite(minLon)) return null;

  const lat0 = (minLat + maxLat) / 2;
  const mPerLon = METER_PER_GRAAD * Math.cos(lat0 * Math.PI / 180);
  const naarX = (lon) => (lon - minLon) * mPerLon;
  const naarY = (lat) => (lat - minLat) * METER_PER_GRAAD;

  const breedteM = naarX(maxLon), hoogteM = naarY(maxLat);
  const nx = Math.max(1, Math.ceil(breedteM / vakM));
  const ny = Math.max(1, Math.ceil(hoogteM / vakM));

  // De vormen één keer omrekenen naar meters, met hun omhullende rechthoek.
  // Zonder die rechthoek zou elk vakje tegen elke sector getoetst worden, en
  // dat is bij honderd sectoren en tienduizenden vakjes te traag.
  const lijst = vormen.map(f => {
    const vlakken = vlakkenVan(f.geometry).map(vlak =>
      vlak.map(ring => ring
        .filter(c => Number.isFinite(c[0]) && Number.isFinite(c[1]))
        .map(c => [naarX(c[0]), naarY(c[1])])));
    let x0 = Infinity, x1 = -Infinity, y0 = Infinity, y1 = -Infinity;
    for (const vlak of vlakken) for (const [x, y] of (vlak[0] || [])) {
      if (x < x0) x0 = x; if (x > x1) x1 = x;
      if (y < y0) y0 = y; if (y > y1) y1 = y;
    }
    return {
      code: f.properties?.NISCODE || null,
      wijkId: f.properties?.WIJK_ID || null,
      naam: f.properties?.NAAM || null,
      vlakken, x0, x1, y0, y1,
    };
  });

  // Per sector een lijst met de kolommen van het raster die hij raakt, zodat
  // een vakje alleen tegen de sectoren in zijn eigen kolom wordt getoetst.
  const perKolom = Array.from({ length: nx }, () => []);
  lijst.forEach((s, i) => {
    const van = Math.max(0, Math.floor(s.x0 / vakM));
    const tot = Math.min(nx - 1, Math.floor(s.x1 / vakM));
    for (let kx = van; kx <= tot; kx++) perKolom[kx].push(i);
  });

  const sectorVanVak = new Int32Array(nx * ny).fill(-1);
  let vakkenBinnen = 0;
  for (let ky = 0; ky < ny; ky++) {
    const y = (ky + 0.5) * vakM;
    for (let kx = 0; kx < nx; kx++) {
      const x = (kx + 0.5) * vakM;
      for (const i of perKolom[kx]) {
        const s = lijst[i];
        if (x < s.x0 || x > s.x1 || y < s.y0 || y > s.y1) continue;
        let raak = false;
        for (const vlak of s.vlakken) {
          if (!vlak.length || !puntInRing(x, y, vlak[0])) continue;
          let inGat = false;
          for (let g = 1; g < vlak.length; g++) {
            if (puntInRing(x, y, vlak[g])) { inGat = true; break; }
          }
          if (!inGat) { raak = true; break; }
        }
        if (raak) { sectorVanVak[ky * nx + kx] = i; vakkenBinnen++; break; }
      }
    }
  }

  const vakkenPerSector = new Int32Array(lijst.length);
  for (let i = 0; i < sectorVanVak.length; i++) {
    const s = sectorVanVak[i];
    if (s >= 0) vakkenPerSector[s]++;
  }

  // Hoeveel adressen er in elk vakje staan. Een adres is een woning, een winkel
  // of een appartement, dus dit is de beste maat voor waar mensen wonen. Zonder
  // adressen blijft alles nul en wordt er hieronder gelijkmatig verdeeld.
  const adresPerVak = new Float32Array(nx * ny);
  const adresPerSector = new Float32Array(lijst.length);
  for (const a of adressen || []) {
    const lat = Number(a?.lat), lng = Number(a?.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
    const kx = Math.floor(naarX(lng) / vakM);
    const ky = Math.floor(naarY(lat) / vakM);
    if (kx < 0 || ky < 0 || kx >= nx || ky >= ny) continue;
    const i = ky * nx + kx;
    const s = sectorVanVak[i];
    if (s < 0) continue;                 // buiten elke sector, telt niet mee
    adresPerVak[i]++;
    adresPerSector[s]++;
  }

  // De inwoners van een sector over zijn vakjes verdelen. Staan er adressen in
  // die sector, dan naar rato van het aantal adressen per vakje: een akker
  // krijgt niets, een straat met huizen krijgt alles. Staan er geen adressen,
  // dan gelijkmatig, zoals het hiervoor werkte.
  const inwPerVak = new Float32Array(nx * ny);
  for (let i = 0; i < sectorVanVak.length; i++) {
    const s = sectorVanVak[i];
    if (s < 0) continue;
    const totaal = inwoners[lijst[s].code];
    if (totaal == null) continue;
    if (adresPerSector[s] > 0) {
      inwPerVak[i] = totaal * (adresPerVak[i] / adresPerSector[s]);
    } else if (vakkenPerSector[s] > 0) {
      inwPerVak[i] = totaal / vakkenPerSector[s];
    }
  }

  return {
    nx, ny, vakM, minLon, minLat, mPerLon, lat0,
    sectoren: lijst, sectorVanVak, inwPerVak, adresPerVak, adresPerSector,
    vakkenPerSector, vakkenBinnen,
    naarX, naarY,
    // Van een rastervakje terug naar graden, voor het tekenen op de kaart.
    lonVan: (kx) => minLon + ((kx + 0.5) * vakM) / mPerLon,
    latVan: (ky) => minLat + ((ky + 0.5) * vakM) / METER_PER_GRAAD,
  };
}

/**
 * Zet per vakje of er binnen straalM meter een laadpunt staat. Er wordt niet
 * per vakje naar alle punten gezocht; in plaats daarvan krijgt elk punt zijn
 * eigen schijfje vakjes gestempeld. Dat scheelt een factor duizend.
 */
export function bepaalDekking(raster, punten, straalM = 250) {
  if (!raster) return null;
  const { nx, ny, vakM, naarX, naarY } = raster;
  const gedekt = new Uint8Array(nx * ny);
  const r = Math.ceil(straalM / vakM);
  const straal2 = straalM * straalM;

  for (const p of (punten || [])) {
    const lat = p.lat, lng = p.lng ?? p.lon;
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
    const px = naarX(lng), py = naarY(lat);
    const kx0 = Math.floor(px / vakM), ky0 = Math.floor(py / vakM);
    for (let ky = ky0 - r; ky <= ky0 + r; ky++) {
      if (ky < 0 || ky >= ny) continue;
      const y = (ky + 0.5) * vakM;
      for (let kx = kx0 - r; kx <= kx0 + r; kx++) {
        if (kx < 0 || kx >= nx) continue;
        const x = (kx + 0.5) * vakM;
        const dx = x - px, dy = y - py;
        if (dx * dx + dy * dy <= straal2) gedekt[ky * nx + kx] = 1;
      }
    }
  }
  return gedekt;
}

/**
 * Maakt van het raster een afbeelding die als laag over de kaart gelegd kan
 * worden: elk vakje ZONDER dekking krijgt kleur, de rest blijft doorzichtig.
 *
 * Waarom een afbeelding en geen vlakken: er zijn tienduizenden vakjes, en die
 * als losse vormen op de kaart zetten maakt hem onbruikbaar traag. Een
 * afbeelding van nx bij ny beeldpunten is even groot als het raster zelf en
 * wordt door de kaart geschaald. Op vijftig meter per vakje zie je de blokjes,
 * en dat is eerlijk: fijner dan het raster weten we het niet.
 *
 * Geeft null terug als er niets te tonen is.
 */
export function maakDekkingBeeld(raster, gedekt, kleur = '#A32A16', doorzichtigheid = 0.85) {
  if (!raster || !gedekt || typeof document === 'undefined') return null;
  const { nx, ny, vakM, minLon, minLat, mPerLon, sectorVanVak, inwPerVak } = raster;

  const doek = document.createElement('canvas');
  doek.width = nx; doek.height = ny;
  const pen = doek.getContext('2d');
  if (!pen) return null;
  const beeld = pen.createImageData(nx, ny);

  const r = parseInt(kleur.slice(1, 3), 16);
  const g = parseInt(kleur.slice(3, 5), 16);
  const b = parseInt(kleur.slice(5, 7), 16);

  // EEN boodschap, EEN kleur: hier wonen mensen zonder laadpunt binnen 250
  // meter. Een vakje is gekleurd of niet, en alle gekleurde vakjes zijn even
  // sterk.
  //
  // Eerder liep de sterkte mee met het aantal inwoners. Dat las verkeerd: een
  // dicht bewoonde buurt werd donker, en dat leest als "hier is de dekking het
  // slechtst" terwijl het "hier wonen veel mensen" betekent. Hoeveel mensen het
  // zijn staat in de tabel onder de kaart; de kaart zegt alleen WAAR.
  //

  // ONBEWOOND GEBIED KRIJGT GEEN KLEUR. Duinen, water, bos en akkers liggen
  // vrijwel altijd verder dan 250 meter van een laadpaal, en dat rood zegt
  // niets: daar komt nooit een laadpunt te staan. Bij De Panne kleurde daardoor
  // de hele duingordel tot in zee.
  //
  // De inwoners van een sector zijn gelijk over zijn vakjes verdeeld, dus een
  // vakje zonder inwoners is hetzelfde als een vakje in een sector zonder
  // inwoners. Fijner dan de sector weten we het niet.
  //
  // Is van geen enkele sector het inwonertal bekend, dan blijft alles kleuren
  // zoals voorheen. Anders zou de laag stilletjes leeg blijven op het moment
  // dat de bevolkingsbron ontbreekt.
  let bewoningBekend = false;
  for (let i = 0; i < inwPerVak.length; i++) {
    if (inwPerVak[i] > 0) { bewoningBekend = true; break; }
  }

  let ongedekt = 0;
  for (let ky = 0; ky < ny; ky++) {
    // De afbeelding loopt van boven naar beneden, het raster van zuid naar
    // noord. Dus omdraaien, anders staat de kaart op zijn kop.
    const rij = ny - 1 - ky;
    for (let kx = 0; kx < nx; kx++) {
      const i = ky * nx + kx;
      if (sectorVanVak[i] < 0 || gedekt[i]) continue;   // buiten de gemeente of gedekt
      if (bewoningBekend && !(inwPerVak[i] > 0)) continue;   // onbewoond
      const p = (rij * nx + kx) * 4;
      beeld.data[p] = r; beeld.data[p + 1] = g; beeld.data[p + 2] = b;
      beeld.data[p + 3] = Math.round(doorzichtigheid * 255);
      ongedekt++;
    }
  }
  if (!ongedekt) return null;
  pen.putImageData(beeld, 0, 0);

  const METER_PER_GRAAD_LOK = 111320;
  return {
    dataUrl: doek.toDataURL('image/png'),
    // [[zuid, west], [noord, oost]], zoals de kaart het verwacht.
    grenzen: [
      [minLat, minLon],
      [minLat + (ny * vakM) / METER_PER_GRAAD_LOK, minLon + (nx * vakM) / mPerLon],
    ],
    ongedekteVakken: ongedekt,
  };
}

/**
 * Telt op hoeveel inwoners en hoeveel oppervlak binnen en buiten de dekking
 * vallen, voor de hele gemeente en per wijk.
 */
export function telDekking(raster, gedekt) {
  if (!raster || !gedekt) return null;
  const { sectorVanVak, inwPerVak, sectoren, vakM } = raster;
  const vakM2 = vakM * vakM;

  const perWijk = new Map();
  const perSector = new Map();
  let inwTotaal = 0, inwGedekt = 0, vakTotaal = 0, vakGedekt = 0;

  for (let i = 0; i < sectorVanVak.length; i++) {
    const s = sectorVanVak[i];
    if (s < 0) continue;
    const inw = inwPerVak[i];
    const wijkId = sectoren[s].wijkId || 'onbekend';
    if (!perWijk.has(wijkId)) {
      perWijk.set(wijkId, { wijkId, inwoners: 0, inwonersGedekt: 0, vakken: 0, vakkenGedekt: 0 });
    }
    const w = perWijk.get(wijkId);
    w.inwoners += inw; w.vakken++;

    // Ook per sector optellen. Dat is het niveau waarop een gemeente er iets
    // mee kan: een wijk zegt in welke hoek van de stad het knelt, een sector
    // zegt welke straten.
    if (!perSector.has(s)) {
      perSector.set(s, { sector: sectoren[s].code, naam: sectoren[s].naam, wijkId,
                         inwoners: 0, inwonersGedekt: 0, vakken: 0, vakkenGedekt: 0 });
    }
    const sec = perSector.get(s);
    sec.inwoners += inw; sec.vakken++;

    inwTotaal += inw; vakTotaal++;
    if (gedekt[i]) {
      w.inwonersGedekt += inw; w.vakkenGedekt++;
      sec.inwonersGedekt += inw; sec.vakkenGedekt++;
      inwGedekt += inw; vakGedekt++;
    }
  }

  const afronden = (o) => ({
    ...o,
    inwoners: Math.round(o.inwoners),
    inwonersGedekt: Math.round(o.inwonersGedekt),
    inwonersZonder: Math.round(o.inwoners - o.inwonersGedekt),
    km2: (o.vakken * vakM2) / 1e6,
    km2Zonder: ((o.vakken - o.vakkenGedekt) * vakM2) / 1e6,
    deelInwoners: o.inwoners > 0 ? o.inwonersGedekt / o.inwoners : 0,
  });

  return {
    totaal: afronden({ inwoners: inwTotaal, inwonersGedekt: inwGedekt,
                       vakken: vakTotaal, vakkenGedekt: vakGedekt }),
    perWijk: [...perWijk.values()].map(afronden),
    perSector: [...perSector.values()].map(afronden),
  };
}

/**
 * Plaatst de nieuwe laadpalen van elk jaar op het raster: de PLAATSINGSMOTOR
 * achter het tabblad Plaatsing.
 *
 * De regel is één getal per vakje, de TEKORTSCORE: de laadpunten die de mensen
 * binnen 250 meter dat jaar nodig hebben, min de laadpunten die er binnen 250
 * meter al staan of in een eerder jaar van dit plan zijn geplot. De paal gaat
 * naar de hoogste score; daarna gaat het aanbod rond die plek met twee punten
 * omhoog en zakt de score daar, zodat de volgende paal verderop landt.
 *
 * Datzelfde getal doet twee dingen tegelijk. Waar niets staat is het aanbod nul
 * en is de score gelijk aan de hele vraag, dus daar wordt eerst gespreid. Is een
 * gebied eenmaal bediend, dan wint de plek waar de vraag het hardst gegroeid is,
 * en dat is verdichting.
 *
 * De jaren worden ACHTER ELKAAR doorgerekend en het aanbod wordt niet
 * teruggezet, dus wat in 2028 is geplot telt in 2029 mee als bestaand.
 *
 * @param {Object} raster    uitkomst van bouwRaster
 * @param {Array}  punten    bestaande laadpunten, elk met lat en lng en
 *                           eventueel punten (aantal laadpunten, standaard 1)
 * @param {Object} opdracht  jaren, losPerWijkJaar, nodigPerWijkJaar,
 *                           pleinenPerJaar, aandeelPvp, voorrang
 */
/**
 * Legt een rooster van 100 meter over de straatpunten, zodat het zoeken naar de
 * dichtstbijzijnde straat niet elke keer alle punten hoeft af te lopen. Bij
 * Leuven zijn dat er tienduizenden en gebeurt het zoeken duizenden keren.
 */
function maakStratenIndex(straten, naarX, naarY, hokM = 100) {
  const hokken = new Map();
  const lijst = [];
  for (const p of (straten || [])) {
    const lat = p.lat, lng = p.lng;
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
    const x = naarX(lng), y = naarY(lat);
    const i = lijst.length;
    lijst.push({ x, y, lat, lng, naam: p.naam || '', soort: p.soort || '' });
    const sleutel = `${Math.floor(x / hokM)}:${Math.floor(y / hokM)}`;
    if (!hokken.has(sleutel)) hokken.set(sleutel, []);
    hokken.get(sleutel).push(i);
  }
  return { hokken, lijst, hokM };
}

// Hoe zwaar een wegsoort meetelt bij het kiezen van een plek. Een woonstraat
// telt zijn echte afstand, een doorgaande weg telt verder weg dan hij ligt.
// Daardoor wint een woonstraat het bijna altijd, en komt een paal alleen aan
// een doorgaande weg als er in de buurt niets anders is. Langs een doorgaande
// weg wordt zelden geparkeerd, en een laadpaal daar is voor een bewoner
// onbruikbaar.
// Een lege wegsoort telt als woonstraat: dat zijn gemeenten waarvan de straten
// zijn opgehaald voordat de wegsoort werd bewaard.
const WEG_GEWICHT = {
  living_street: 1.0, residential: 1.0, pedestrian: 1.0, unclassified: 1.0,
  tertiary: 1.6, tertiary_link: 1.6,
  secondary: 2.6, secondary_link: 2.6,
  primary: 3.4, primary_link: 3.4,
};
function wegGewicht(soort) {
  return WEG_GEWICHT[soort] != null ? WEG_GEWICHT[soort] : 1.0;
}

/**
 * Het dichtstbijzijnde straatpunt binnen maxM meter, of null. gebruikt is een
 * verzameling met de al ingenomen straatpunten, zodat twee palen niet op
 * dezelfde meter belanden. Niet de kortste afstand wint, maar de kortste
 * gewogen afstand: zie WEG_GEWICHT hierboven.
 */
function dichtstbijStraat(index, x, y, maxM, gebruikt) {
  if (!index || !index.lijst.length) return null;
  const { hokken, lijst, hokM } = index;
  const straal = Math.ceil(maxM / hokM);
  const hx = Math.floor(x / hokM), hy = Math.floor(y / hokM);
  let beste = null, besteGewogen = Infinity;
  for (let a = hx - straal; a <= hx + straal; a++) {
    for (let b = hy - straal; b <= hy + straal; b++) {
      for (const i of (hokken.get(`${a}:${b}`) || [])) {
        if (gebruikt && gebruikt.has(i)) continue;
        const p = lijst[i];
        const d = Math.hypot(p.x - x, p.y - y);
        // De echte afstand blijft de grens: een paal wordt nooit verder
        // getrokken dan maxM meter, ook niet naar een woonstraat.
        if (d > maxM) continue;
        const gewogen = d * wegGewicht(p.soort);
        if (gewogen < besteGewogen) {
          besteGewogen = gewogen;
          beste = { i, ...p, afstand: Math.round(d) };
        }
      }
    }
  }
  return beste;
}

export function plaatsPalen(raster, punten, opdracht = {}) {
  if (!raster) return null;
  const { nx, ny, vakM, sectorVanVak, inwPerVak, sectoren, naarX, naarY, lonVan, latVan } = raster;

  const straalM   = opdracht.straalM   ?? 250;
  const maxPerVak = opdracht.maxPerVak ?? 4;    // vier laadpunten op één aansluiting
  const perPaal   = opdracht.perPaal   ?? 2;    // één paal voedt twee laadpunten
  const voorrang  = opdracht.voorrang  ?? 1;    // opslag voor plekken zonder dekking
  const aandeelPvp = opdracht.aandeelPvp ?? 0;  // deel dat als Paal volgt Paal gaat
  const jaren = opdracht.jaren || [];
  // Het aantal laadpunten dat er dit jaar in deze wijk bij komt. Wat de
  // aangewezen laadpleinen daarvan opvangen gaat er hieronder vanaf; de rest
  // wordt als losse paal geplot. Zo kan er geen laadpunt meer uit het plan
  // vallen doordat er wel een pleinaandeel wordt afgetrokken maar geen plein is
  // aangewezen.
  const jaardeel = opdracht.jaardeelPerWijkJaar || {};
  const nodig = opdracht.nodigPerWijkJaar || {};
  const pleinen = opdracht.pleinenPerJaar || {};

  // De wijk van elk aangewezen laadplein, en per wijk wat de pleinen van de
  // eigen wijk opvangen (stap A van de regel van HL, 27 oktober 2026). Zie
  // pleinEigenWijk hieronder.
  const pleinLijst = [];
  for (const jaar of jaren) {
    for (const l of (pleinen?.[jaar] || [])) {
      const w = wijkVanPunt(raster, l.lat, l.lng);
      if (!w || !(Number(l.punten) > 0)) continue;
      pleinLijst.push({ ...l, jaar, wijkId: w, punten: Number(l.punten) });
    }
  }
  // De grens: het laatste jaar van de planperiode (Parameters, Periode).
  // Besluit HL 28 oktober 2026: niet 2035, want een gemeente als Leuven wil
  // een plan tot en met 2030.
  const laatste = jaren.length ? jaren[jaren.length - 1] : null;
  const eindJaar = jaren.length
    ? Math.max(jaren[0], Math.min(Number(opdracht.eindJaar) || laatste, laatste)) : null;
  const jarenTot = jaren.filter(j => j <= eindJaar);
  const verdelingA = pleinEigenWijk(jarenTot, jaardeel, pleinLijst);
  const loopM = opdracht.loopM ?? 250;
  // Na het eindjaar vangt wat een plein dan nog over heeft weer de eigen wijk
  // op, zodat er geen losse palen naast een half leeg plein komen. Gevuld na
  // stap B, aan het eind van het eindjaar.
  const potNa = {};
  const eigenNa = {};
  let verdelingB = { overgenomen: new Map(), naarAndere: {}, restNa: { ...verdelingA.rest } };

  const n = nx * ny;
  const rVak = Math.ceil(straalM / vakM);
  const straal2 = straalM * straalM;

  // Welke vakjes horen bij welke wijk, en welke daarvan zijn bewoond. Een paal
  // in een akker of in zee heeft geen zin, dus onbewoonde vakjes doen niet mee
  // als kandidaat. Dezelfde regel als bij het rood op de dekkingskaart.
  const kandidatenPerWijk = new Map();
  for (let i = 0; i < n; i++) {
    const s = sectorVanVak[i];
    if (s < 0 || !(inwPerVak[i] > 0)) continue;
    const wid = sectoren[s].wijkId || 'onbekend';
    if (!kandidatenPerWijk.has(wid)) kandidatenPerWijk.set(wid, []);
    kandidatenPerWijk.get(wid).push(i);
  }

  // Rond elk vakje een schijf van 250 meter. Deze functie loopt die schijf af.
  const overSchijf = (kx, ky, doe) => {
    const cx = (kx + 0.5) * vakM, cy = (ky + 0.5) * vakM;
    for (let y = ky - rVak; y <= ky + rVak; y++) {
      if (y < 0 || y >= ny) continue;
      const dy = (y + 0.5) * vakM - cy;
      for (let x = kx - rVak; x <= kx + rVak; x++) {
        if (x < 0 || x >= nx) continue;
        const dx = (x + 0.5) * vakM - cx;
        if (dx * dx + dy * dy <= straal2) doe(y * nx + x);
      }
    }
  };

  // Het aanbod: laadpunten binnen 250 meter van een vakje. En per vakje hoeveel
  // laadpunten er in dat vakje zelf staan, want dat bepaalt of er nog een paal
  // op dezelfde aansluiting bij kan.
  const aanbod = new Float32Array(n);
  const puntenVak = new Float32Array(n);
  const stempel = (kx, ky, aantal) => overSchijf(kx, ky, (j) => { aanbod[j] += aantal; });

  const vakVan = (lat, lng) => {
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    const kx = Math.floor(naarX(lng) / vakM), ky = Math.floor(naarY(lat) / vakM);
    if (kx < 0 || kx >= nx || ky < 0 || ky >= ny) return null;
    return { kx, ky, i: ky * nx + kx };
  };

  for (const p of (punten || [])) {
    const v = vakVan(p.lat, p.lng ?? p.lon);
    if (!v) continue;
    const aantal = Number(p.punten) > 0 ? Number(p.punten) : 1;
    puntenVak[v.i] += aantal;
    stempel(v.kx, v.ky, aantal);
  }

  // Hoeveel GEWONE laadpunten er bij aanvang al staan. Nodig voor Paal volgt
  // Paal: het aandeel dat de bijplaatsnorm haalt geldt over deze BESTAANDE
  // voorraad, niet over wat er dit jaar bij komt.
  //
  // Alleen gewone laadpunten tellen mee, dus niet de snelladers. Een tweede
  // gewone paal zet je naast een gewone paal; een snellader heeft een andere
  // aansluiting en een ander gebruikspatroon. Ultrasnelladers zitten sowieso
  // niet in punten, die worden er in het scherm al uitgehaald.
  //
  // Het aantal per vakje staat in puntenVak, maar dat groeit tijdens het
  // plaatsen, dus het totaal wordt hier eenmalig vastgelegd.
  let bestaandeLaadpunten = 0;
  for (const p of (punten || [])) {
    if (!vakVan(p.lat, p.lng ?? p.lon)) continue;
    if (p.snel) continue;                       // snellader telt niet mee
    bestaandeLaadpunten += Number(p.punten) > 0 ? Number(p.punten) : 1;
  }

  // Hoeveel tweede palen er per vakje bij kunnen. Elke paal die er al staat kan
  // er een tweede naast krijgen op dezelfde aansluiting, dus de ruimte is het
  // aantal palen in dat vakje. Staan er zes laadpunten, dan zijn dat drie palen
  // en kunnen er drie tweede palen bij.
  //
  // Voor 19 augustus 2026 gold hier een grens van vier LAADPUNTEN per vakje.
  // Dat is een grens per aansluiting, terwijl een vakje van 50 meter meerdere
  // aansluitingen kan bevatten. Daardoor telde bijna elk vakje met laadpunten
  // als vol en kwam er van 150 gevraagde laadpunten maar 42 terecht.
  const pvpRuimteVak = new Int32Array(n);
  for (let i = 0; i < n; i++) pvpRuimteVak[i] = Math.floor(puntenVak[i] / perPaal);

  // Het gewicht van een vakje binnen zijn wijk, om de behoefte van die wijk
  // over de vakjes te verdelen. Standaard het aantal AUTO'S van de sector,
  // gelijk verdeeld over zijn vakjes: dezelfde sleutel als de kaart op het
  // tabblad Gemeente, zodat de twee kaarten hetzelfde patroon laten zien. Is
  // van een sector geen gewicht bekend, dan valt hij terug op het inwonertal.
  const gewichtSector = opdracht.gewichtPerSector || null;
  const vakkenPerSectorTel = new Map();
  for (const [, lijst] of kandidatenPerWijk) {
    for (const i of lijst) {
      const code = sectoren[sectorVanVak[i]].code;
      vakkenPerSectorTel.set(code, (vakkenPerSectorTel.get(code) || 0) + 1);
    }
  }
  const gewichtVak = new Float32Array(n);
  for (const [, lijst] of kandidatenPerWijk) {
    for (const i of lijst) {
      const code = sectoren[sectorVanVak[i]].code;
      const g = gewichtSector ? gewichtSector[code] : null;
      const aantal = vakkenPerSectorTel.get(code) || 1;
      gewichtVak[i] = (g != null && g > 0) ? g / aantal : inwPerVak[i];
    }
  }
  const gewichtPerWijk = new Map();
  for (const [wid, lijst] of kandidatenPerWijk) {
    let som = 0;
    for (const i of lijst) som += gewichtVak[i];
    gewichtPerWijk.set(wid, som);
  }

  // Dezelfde kandidaatvakjes, maar nu per sector binnen de wijk, met het
  // gewicht van die sector erbij. Nodig om de palen van een wijk over zijn
  // sectoren te verdelen op dezelfde manier als de kaart op tabblad Gemeente
  // dat doet. Zonder die stap wint elke ronde de compactste sector van de wijk
  // en krijgt een ruim opgezette sector jaar na jaar niets.
  const sectorenPerWijk = new Map();
  for (const [wid, lijst] of kandidatenPerWijk) {
    const per = new Map();
    for (const i of lijst) {
      const code = sectoren[sectorVanVak[i]].code;
      if (!per.has(code)) per.set(code, { vakken: [], gewicht: 0 });
      const r = per.get(code);
      r.vakken.push(i);
      r.gewicht += gewichtVak[i];
    }
    sectorenPerWijk.set(wid, per);
  }

  // De straten waar een paal naartoe wordt getrokken. Zonder deze bron blijft
  // een paal op het middelpunt van zijn rastervakje staan, en dan ontstaat er
  // een net rooster van punten dat ruimtelijk nergens op slaat.
  const stratenIndex = maakStratenIndex(opdracht.straten, naarX, naarY);
  const straatMaxM = opdracht.straatMaxM ?? 150;
  const straatGebruikt = new Set();

  const perJaar = {};
  const vraag = new Float32Array(n);
  const vraagSchijf = new Float32Array(n);

  // Paal volgt Paal, over de jaren heen.
  //
  // Het aandeel dat de bijplaatsnorm haalt geldt over de laadpunten die er aan
  // het begin van een jaar al staan. Zet je er een paal bij, dan verdeelt de
  // afzet zich over vier laadpunten in plaats van twee en zakken zowel de oude
  // als de nieuwe paal onder de norm. Beide tellen daarna dus niet meer mee.
  // Daarom groeit uitgesloten met TWEE keer wat er geplaatst is.
  //
  // De voorraad groeit wel met de palen die op een nieuwe plek zijn gezet: die
  // kunnen in een later jaar alsnog de norm halen.
  let voorraadPvp = bestaandeLaadpunten;   // laadpunten die aan het begin van het jaar staan
  let uitgeslotenPvp = 0;                  // daarvan al verdubbeld, plus hun tweede paal

  for (const jaar of jaren) {
    // 1. De vraag van dit jaar per vakje, en daarna opgeteld over de schijf van
    //    250 meter eromheen. De vraag verandert binnen een jaar niet, dus dit
    //    hoeft maar één keer per jaar.
    vraag.fill(0); vraagSchijf.fill(0);
    for (const [wid, lijst] of kandidatenPerWijk) {
      const totaal = nodig?.[wid]?.[jaar] || 0;
      const som = gewichtPerWijk.get(wid) || 0;
      if (!(totaal > 0) || !(som > 0)) continue;
      for (const i of lijst) vraag[i] = totaal * (gewichtVak[i] / som);
    }
    for (let ky = 0; ky < ny; ky++) {
      for (let kx = 0; kx < nx; kx++) {
        const i = ky * nx + kx;
        if (!(vraag[i] > 0)) continue;
        const v = vraag[i];
        overSchijf(kx, ky, (j) => { vraagSchijf[j] += v; });
      }
    }

    // 2. De aangewezen laadpleinen van dit jaar tellen mee als aanbod, want ze
    //    staan er aan het eind van dit jaar. Ze worden niet door de score
    //    gekozen; de gemeente heeft ze zelf aangewezen.
    const pleinPlekken = [];
    const pleinPerWijk = new Map();
    for (const l of (pleinen?.[jaar] || [])) {
      const v = vakVan(l.lat, l.lng);
      const aantal = Number(l.punten) > 0 ? Number(l.punten) : 0;
      if (v && aantal > 0) {
        puntenVak[v.i] += aantal;
        stempel(v.kx, v.ky, aantal);
        const s = sectorVanVak[v.i];
        const pleinWijk = s >= 0 ? (sectoren[s].wijkId || 'onbekend') : 'onbekend';
        pleinPerWijk.set(pleinWijk, (pleinPerWijk.get(pleinWijk) || 0) + aantal);
        pleinPlekken.push({
          soort: 'plein', jaar, punten: aantal, naam: l.naam || null,
          lat: l.lat, lng: l.lng, kx: v.kx, ky: v.ky,
          wijkId: s >= 0 ? (sectoren[s].wijkId || null) : null,
          sector: s >= 0 ? sectoren[s].code : null,
          sectorNaam: s >= 0 ? sectoren[s].naam : null,
        });
      }
    }

    // 2b. Paal volgt Paal voor dit jaar, voor de hele gemeente in een keer.
    //
    // Het aandeel geldt over de laadpunten die nog in aanmerking komen: de
    // voorraad aan het begin van dit jaar, min wat er eerder al verdubbeld is.
    // Dat aantal laadpunten wordt gedeeld door twee, want een tweede paal
    // levert twee laadpunten.
    const inAanmerking = Math.max(0, voorraadPvp - uitgeslotenPvp);
    let pvpPuntenBeschikbaar = Math.round(inAanmerking * aandeelPvp);
    let pvpPalenBeschikbaar = Math.floor(pvpPuntenBeschikbaar / perPaal);

    // Verdelen over de wijken naar rato van de laadpunten die daar al staan.
    // Houdt een wijk ruimte over omdat er te weinig laadpunten bij komen, dan
    // schuift die door naar een wijk die tekort komt; anders zou het stadsbrede
    // aandeel niet gehaald worden. Zodra er per laadpunt bekend is welke de
    // norm haalt, vervalt deze verdeelsleutel.
    const puntenPerWijkNu = new Map();
    let puntenTotaalNu = 0;
    for (const [wid, lijst] of kandidatenPerWijk) {
      let som = 0;
      for (const i of lijst) som += puntenVak[i];
      puntenPerWijkNu.set(wid, som);
      puntenTotaalNu += som;
    }
    const pvpQuotum = new Map();
    for (const [wid, som] of puntenPerWijkNu) {
      pvpQuotum.set(wid, puntenTotaalNu > 0
        ? Math.round(pvpPalenBeschikbaar * (som / puntenTotaalNu)) : 0);
    }
    let pvpRest = Math.max(0, pvpPalenBeschikbaar -
      [...pvpQuotum.values()].reduce((a, b) => a + b, 0));
    let pvpGezetDitJaar = 0;

    // 2c. Hoeveel palen er dit jaar in totaal bij komen, en hoe die over de
    //     wijken worden verdeeld.
    //
    //     Per wijk apart afronden zou het totaal optillen: een wijk met 9
    //     laadpunten kreeg 5 palen en dus 10 laadpunten, en over een gemeente
    //     met twaalf wijken loopt dat op tot een paar laadpunten meer dan het
    //     rekenmodel vraagt. Daarom wordt eerst het totaal bepaald en daarna
    //     verdeeld naar het aandeel van elke wijk. Zo staat er op het scherm
    //     hetzelfde aantal als in de tegel en op tabblad Wijken.
    const losPerWijk = {};
    let losTotaal = 0;
    for (const [wid] of kandidatenPerWijk) {
      const jdW = Math.round(jaardeel?.[wid]?.[jaar] || 0);
      // Wat de pleinen van deze wijk dit jaar opvangen, ook een plein uit een
      // eerder jaar dat nog laadpunten over had.
      let opvang = verdelingA.eigen[wid]?.[jaar] || 0;
      if (jaar > eindJaar) {
        for (const l of pleinLijst) if (l.wijkId === wid && l.jaar === jaar) potNa[wid] = (potNa[wid] || 0) + l.punten;
        opvang = Math.min(potNa[wid] || 0, jdW);
        if (opvang > 0) {
          potNa[wid] -= opvang;
          (eigenNa[wid] = eigenNa[wid] || {})[jaar] = opvang;
        }
      }
      const los = Math.max(0, jdW - opvang);
      losPerWijk[wid] = los;
      losTotaal += los;
    }
    const palenQuotum = verdeelHeel(Math.round(losTotaal / perPaal), losPerWijk);

    // 3. De losse palen, wijk voor wijk.
    const plekken = [...pleinPlekken];
    const perWijk = {};
    for (const [wid, lijst] of kandidatenPerWijk) {
      const jd = Math.round(jaardeel?.[wid]?.[jaar] || 0);
      const pleinPunten = pleinPerWijk.get(wid) || 0;
      const losPunten = losPerWijk[wid] || 0;
      // Wat een plein dit jaar niet in zijn eigen wijk kwijt kan, schuift door
      // naar de volgende jaren van die wijk. Teveel is daarom altijd nul; het
      // veld blijft bestaan voor wie het nog leest.
      const teveel = 0;
      const viaPlein = (verdelingA.eigen[wid]?.[jaar] || 0) + (eigenNa[wid]?.[jaar] || 0);
      const palen = palenQuotum[wid] || 0;
      if (palen <= 0) {
        perWijk[wid] = { jaardeel: jd, plein: pleinPunten, viaPlein, overgenomen: 0, teveel, losPunten,
                         pvp: 0, nieuw: 0, geplaatst: 0, terugval: 0 };
        continue;
      }

      // Hoeveel palen als Paal volgt Paal in deze wijk: het quotum uit 2b,
      // aangevuld met wat andere wijken hebben laten liggen, en begrensd door
      // het aantal palen dat er in deze wijk uberhaupt bij komt.
      //
      // De vakjesgrens (maxPerVak) bepaalt WAAR een tweede paal landt, niet
      // HOEVEEL het er zijn. Voor 19 augustus 2026 begrensde die grens ook het
      // aantal, en dan kwam er van 150 laadpunten maar 42 terecht omdat er per
      // vakje van 50 meter maar vier laadpunten mogen staan. Dat is een grens
      // per aansluiting, en in een vakje van 50 meter liggen er meestal meer.
      let pvpTeGaan = Math.min(palen, (pvpQuotum.get(wid) || 0) + pvpRest);
      pvpRest = Math.max(0, (pvpQuotum.get(wid) || 0) + pvpRest - pvpTeGaan);

      // Het beste vakje zoeken. bezetGevraagd bepaalt of we bij een BESTAANDE
      // paal willen bijzetten of juist op een lege plek. Zonder die scheiding
      // zou een paal bij toeval op een vakje landen waar al iets staat, en dan
      // zou er een tweede paal ontstaan terwijl de schuif op nul staat. Dat is
      // precies wat HL op 17 augustus 2026 zag: Paal volgt Paal op 0 procent en
      // toch zestig tweede palen in het overzicht.
      const kiesVakje = (bezetGevraagd, uit = lijst) => {
        let beste = -1, besteScore = -Infinity;
        for (const i of uit) {
          const bezet = puntenVak[i] > 0;
          if (bezet !== bezetGevraagd) continue;
          // Een tweede paal kan alleen waar nog een paal zonder buurman staat.
          if (bezetGevraagd && pvpRuimteVak[i] <= 0) continue;
          let score = vraagSchijf[i] - aanbod[i];
          if (!(aanbod[i] > 0)) score *= voorrang;               // niets binnen 250 meter
          if (score > besteScore) { besteScore = score; beste = i; }
        }
        return { beste, besteScore };
      };

      // De palen van deze wijk verdelen over de sectoren, naar hetzelfde
      // gewicht als de kaart gebruikt, en daarna per sector de beste plek
      // kiezen. Een sector met veel auto's krijgt er meer, een kleine sector
      // krijgt er in ieder geval een zodra zijn deel op een hele paal uitkomt.
      // Alleen palen op een NIEUWE plek volgen deze verdeling; een tweede paal
      // moet naast een bestaande staan en is dus plaatsgebonden.
      const perSector = sectorenPerWijk.get(wid) || new Map();
      const sectorGewichten = {};
      for (const [code, r] of perSector) sectorGewichten[code] = r.gewicht;
      const sectorQuotum = verdeelHeel(palen, sectorGewichten);
      const wachtrij = Object.entries(sectorQuotum)
        .sort((a, b) => b[1] - a[1])
        .flatMap(([code, aantal]) => Array.from({ length: aantal }, () => code));
      let wachtIndex = 0;

      let gezetPvp = 0, gezetNieuw = 0, terugval = 0;
      for (let k = 0; k < palen; k++) {
        const wilPvp = pvpTeGaan > 0;
        let { beste, besteScore } = kiesVakje(wilPvp);
        if (!wilPvp) {
          // De sector die aan de beurt is, met de hele wijk als terugval zodra
          // daar geen plek meer over is.
          const code = wachtrij[wachtIndex++];
          const vakken = code ? perSector.get(code)?.vakken : null;
          if (vakken && vakken.length) {
            const uitSector = kiesVakje(false, vakken);
            if (uitSector.beste >= 0) ({ beste, besteScore } = uitSector);
          }
        }
        // Is er van de gevraagde soort niets meer, dan mag de andere alsnog.
        // Zonder die terugval zouden palen blijven liggen.
        if (beste < 0) { ({ beste, besteScore } = kiesVakje(!wilPvp)); if (beste >= 0) terugval++; }
        if (beste < 0) break;                                    // nergens meer plek

        const kx = beste % nx, ky = Math.floor(beste / nx);
        const soort = puntenVak[beste] > 0 ? 'pvp' : 'nieuw';
        puntenVak[beste] += perPaal;
        stempel(kx, ky, perPaal);
        if (soort === 'pvp') {
          // Die paal is nu verdubbeld en kan er geen derde bij krijgen.
          pvpRuimteVak[beste] = Math.max(0, pvpRuimteVak[beste] - 1);
          gezetPvp++; if (pvpTeGaan > 0) pvpTeGaan--;
        } else {
          // Een paal op een nieuwe plek kan in een later jaar zelf de norm
          // halen en dan een tweede paal krijgen.
          pvpRuimteVak[beste] += 1;
          gezetNieuw++;
        }

        const s = sectorVanVak[beste];
        // Naar de dichtstbijzijnde straat trekken. Lukt dat niet binnen de
        // grens, dan blijft de plek op het middelpunt van het vakje staan en
        // zegt straatVer dat erbij.
        const straat = dichtstbijStraat(stratenIndex, (kx + 0.5) * vakM, (ky + 0.5) * vakM,
                                        straatMaxM, straatGebruikt);
        if (straat) straatGebruikt.add(straat.i);
        plekken.push({
          soort, jaar, punten: perPaal, kx, ky,
          lat: straat ? straat.lat : latVan(ky),
          lng: straat ? straat.lng : lonVan(kx),
          straat: straat ? (straat.naam || null) : null,
          straatVer: !straat,
          wijkId: wid,
          sector: s >= 0 ? sectoren[s].code : null,
          sectorNaam: s >= 0 ? sectoren[s].naam : null,
          score: Math.round(besteScore * 10) / 10,
          geenDekking: !(aanbod[beste] - perPaal > 0),
        });
      }
      pvpGezetDitJaar += gezetPvp;
      perWijk[wid] = { jaardeel: jd, plein: pleinPunten, viaPlein, overgenomen: 0, teveel, losPunten, terugval,
                       pvp: gezetPvp * perPaal, nieuw: gezetNieuw * perPaal,
                       geplaatst: (gezetPvp + gezetNieuw) * perPaal };
    }

    // 4. De tekortscore per sector, voor de kleur op de kaart. Gemiddeld over de
    //    bewoonde vakjes van de sector, gemeten VOOR de palen van dit jaar
    //    zouden zijn geplaatst is niet mogelijk zonder een tweede doorloop, dus
    //    dit is de stand aan het eind van het jaar.
    const scorePerSector = {};
    for (let i = 0; i < n; i++) {
      const s = sectorVanVak[i];
      if (s < 0 || !(inwPerVak[i] > 0)) continue;
      const code = sectoren[s].code;
      if (!scorePerSector[code]) scorePerSector[code] = { som: 0, aantal: 0 };
      scorePerSector[code].som += Math.max(0, vraagSchijf[i] - aanbod[i]);
      scorePerSector[code].aantal++;
    }
    const tekortSector = {};
    for (const code of Object.keys(scorePerSector)) {
      const o = scorePerSector[code];
      tekortSector[code] = o.aantal ? o.som / o.aantal : 0;
    }

    // De boekhouding voor Paal volgt Paal bijwerken voor het volgende jaar.
    // Elke tweede paal sluit twee laadpunten uit: de paal die de norm haalde en
    // de paal die ernaast is gezet. Samen zakken ze onder de norm, want de afzet
    // verdeelt zich over vier laadpunten in plaats van twee.
    uitgeslotenPvp += pvpGezetDitJaar * perPaal * 2;
    // De voorraad groeit met alles wat er dit jaar is bij gekomen: de tweede
    // palen, de palen op een nieuwe plek en de laadpunten op een laadplein.
    for (const r of Object.values(perWijk)) voorraadPvp += (r.geplaatst || 0) + (r.plein || 0);

    perJaar[jaar] = { plekken, perWijk, tekortSector,
                      pvpVoorraad: voorraadPvp, pvpUitgesloten: uitgeslotenPvp,
                      pvpBeschikbaar: pvpPuntenBeschikbaar, pvpGezet: pvpGezetDitJaar * perPaal };
    if (jaar === eindJaar) {
      voorraadPvp -= stapB();
      for (const [wid, n] of Object.entries(verdelingB.restNa)) potNa[wid] = (potNa[wid] || 0) + n;
    }
  }

  // Stap B, aan het eind van het eindjaar: wat een plein tot en met dat jaar
  // niet in zijn eigen wijk kwijt kan, neemt losse laadpunten over in andere
  // wijken binnen de loopafstand, gepland tot en met het eindjaar. Die vallen
  // hier uit de plaatsing. Ze worden niet opnieuw geplot: een herplotting zou
  // de palen verschuiven en dan klopt de keuze niet meer met de kaart. Geeft
  // het aantal weggevallen laadpunten terug, voor de voorraad van Paal volgt Paal.
  function stapB() {
  const losse = [];
  for (const jaar of jarenTot) {
    for (const p of (perJaar[jaar]?.plekken || [])) if (p.soort !== 'plein') losse.push(p);
  }
  verdelingB = pleinAndereWijken(pleinLijst, verdelingA, losse, loopM, eindJaar);
  let weg = 0;
  for (const jaar of jarenTot) {
    const pj = perJaar[jaar];
    if (!pj) continue;
    pj.vervallen = [];
    pj.plekken = pj.plekken.filter(p => {
      const o = verdelingB.overgenomen.get(p);
      if (!o) return true;
      pj.vervallen.push({ ...p, doorWijk: o.doorWijk, afstand: o.afstand });
      const w = pj.perWijk[p.wijkId];
      if (w) {
        w.overgenomen = (w.overgenomen || 0) + p.punten;
        w.viaPlein = (w.viaPlein || 0) + p.punten;
        w.geplaatst = Math.max(0, (w.geplaatst || 0) - p.punten);
        w.losPunten = Math.max(0, (w.losPunten || 0) - p.punten);
        if (p.soort === 'pvp') w.pvp = Math.max(0, (w.pvp || 0) - p.punten);
        else w.nieuw = Math.max(0, (w.nieuw || 0) - p.punten);
      }
      weg += p.punten;
      return false;
    });
  }
  return weg;
  }

  for (const jaar of jaren) if (perJaar[jaar] && !perJaar[jaar].vervallen) perJaar[jaar].vervallen = [];
  return { perJaar, pleinImpact: { loopM, eindJaar, pleinen: pleinLijst, ...verdelingA, ...verdelingB, eigenNa } };
}

// ── Laadpleinen per wijk, regel van HL van 27 oktober 2026 ────────────
//
// Alles tot en met het laatste jaar van de planperiode (besluit HL 28 oktober 2026).
// A. Een laadplein vangt eerst de nieuwe laadpunten van zijn eigen wijk op,
//    te beginnen in het jaar van plaatsing en dan jaar na jaar.
// B. Is de behoefte van de eigen wijk tot en met het eindjaar opgevangen en
//    blijft er nog iets over, dan neemt het plein losse laadpunten over in
//    andere wijken binnen de loopafstand, gepland NA het jaar van plaatsing,
//    de dichtstbijzijnde eerst. Wat tot en met dat jaar geplot is, blijft staan.
//
// Meerdere pleinen in een wijk tellen samen; het vroegste jaar geldt als jaar
// van plaatsing voor stap B.

/** De wijk van een punt, via de sector van het rastervakje. Null buiten het raster. */
export function wijkVanPunt(raster, lat, lng) {
  if (!raster || !Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  const { nx, ny, vakM, sectorVanVak, sectoren, naarX, naarY } = raster;
  const kx = Math.floor(naarX(lng) / vakM), ky = Math.floor(naarY(lat) / vakM);
  if (kx < 0 || kx >= nx || ky < 0 || ky >= ny) return null;
  const s = sectorVanVak[ky * nx + kx];
  return s >= 0 ? (sectoren[s].wijkId || null) : null;
}

/**
 * Stap A. jaardeel: wijk -> jaar -> nieuwe laadpunten. pleinen: lijst met
 * wijkId, jaar en punten. Geeft per wijk wat de pleinen per jaar opvangen,
 * wat er na het laatste jaar over is, en het vroegste jaar van plaatsing.
 */
export function pleinEigenWijk(jaren, jaardeel, pleinen) {
  const perWijkJaar = {};
  for (const l of (pleinen || [])) {
    const o = (perWijkJaar[l.wijkId] = perWijkJaar[l.wijkId] || {});
    o[l.jaar] = (o[l.jaar] || 0) + (Number(l.punten) || 0);
  }
  const eigen = {}, rest = {}, eersteJaar = {}, puntenWijk = {};
  for (const [wid, perJaar] of Object.entries(perWijkJaar)) {
    let pot = 0;
    eigen[wid] = {};
    for (const jaar of jaren) {
      const erbij = perJaar[jaar] || 0;
      if (erbij > 0 && eersteJaar[wid] == null) eersteJaar[wid] = jaar;
      pot += erbij;
      puntenWijk[wid] = (puntenWijk[wid] || 0) + erbij;
      const vraag = Math.round(jaardeel?.[wid]?.[jaar] || 0);
      const t = Math.min(pot, vraag);
      if (t > 0) { eigen[wid][jaar] = t; pot -= t; }
    }
    rest[wid] = pot;
  }
  return { eigen, rest, eersteJaar, puntenWijk };
}

function meterTussenPunten(a, b) {
  const r = Math.PI / 180;
  const dl = (b.lat - a.lat) * r, dg = (b.lng - a.lng) * r;
  const h = Math.sin(dl / 2) ** 2 + Math.cos(a.lat * r) * Math.cos(b.lat * r) * Math.sin(dg / 2) ** 2;
  return 2 * 6371008 * Math.asin(Math.sqrt(h));
}

/**
 * Stap B. losse: de geplotte losse palen (alle jaren), elk met lat, lng,
 * jaar, wijkId en punten. Een paal valt alleen helemaal weg, nooit half.
 * Geeft overgenomen (Map van paal naar { doorWijk, afstand }) en per wijk de
 * lijst van overgenomen palen; rest wordt bijgewerkt in een kopie.
 */
export function pleinAndereWijken(pleinen, verdelingA, losse, loopM = 250, totEnMet = Infinity) {
  const overgenomen = new Map();
  const naarAndere = {};
  const restNa = { ...(verdelingA?.rest || {}) };
  const wijken = Object.keys(restNa).filter(w => restNa[w] > 0)
    .sort((a, b) => (verdelingA.eersteJaar[a] - verdelingA.eersteJaar[b]) || String(a).localeCompare(String(b)));
  for (const A of wijken) {
    const eigenPleinen = (pleinen || []).filter(l => l.wijkId === A);
    const Y = verdelingA.eersteJaar[A];
    const kand = [];
    for (const p of (losse || [])) {
      if (p.wijkId === A || !(p.jaar > Y) || p.jaar > totEnMet || overgenomen.has(p)) continue;
      let d = Infinity;
      for (const l of eigenPleinen) d = Math.min(d, meterTussenPunten(l, p));
      if (d <= loopM) kand.push({ p, d });
    }
    kand.sort((a, b) => a.d - b.d);
    naarAndere[A] = [];
    for (const { p, d } of kand) {
      if (!(restNa[A] > 0)) break;
      if (p.punten > restNa[A]) continue;
      overgenomen.set(p, { doorWijk: A, afstand: Math.round(d) });
      naarAndere[A].push({ plek: p, punten: p.punten, afstand: Math.round(d) });
      restNa[A] -= p.punten;
    }
  }
  return { overgenomen, naarAndere, restNa };
}
